<template>
  <div class="pa-1 mt-4">
    <!-- Desktop View -->
    <v-data-table
      v-if="!device.isMobile"
      :headers="headers"
      :items="users"
      class="striped-table ordera-box-shadow"
      hide-default-footer
      disable-pagination
      mobile-breakpoint="0"
      :loading="loading"
    >
      <template v-slot:no-data>
        <v-container class="text-center py-12">
          <v-icon size="64" color="grey lighten-1" class="mb-4"> mdi-account-off </v-icon>
          <div class="text-h6 grey--text text--darken-1 mb-2">
            {{ $t("no_data") }}
          </div>
          <div class="text-body-2 grey--text">
            {{ $t("select_date_and_filters") }}
          </div>
        </v-container>
      </template>

      <template v-slot:item.user_name="{ item }">
        <span @click="openUserDialog(item)" class="cursor-pointer">
          {{ toLatin(item.user_name) }}
        </span>
      </template>

      <template v-slot:item.orders_total="{ item }">
        {{ formatMoneyField(item, "orders_total") }}
      </template>

      <template v-slot:item.spent_on_credits="{ item }">
        {{ formatMoneyField(item, "spent_on_credits") }}
      </template>

      <template v-slot:item.current_credit_balance="{ item }">
        {{ formatMoneyField(item, "current_credit_balance") }}
      </template>

      <template v-slot:item.action="{ item }">
        <v-row no-gutters align="center" justify="end">
          <circle-button
            icon="mdi-format-list-bulleted"
            @click="openUserDialog(item)"
            color="primary"
            :tooltip="$t('tooltip_order_details')"
            class="mr-1"
          ></circle-button>
          <circle-button
            icon="mdi-history"
            @click="openHistoryDialog(item)"
            color="info"
            :tooltip="$t('topup_history')"
            class="mr-1"
          ></circle-button>
          <circle-button
            icon="mdi-cash-plus"
            @click="navigateToTopup(item)"
            color="success"
            :tooltip="$t('add_credit')"
          ></circle-button>
        </v-row>
      </template>
    </v-data-table>

    <!-- Mobile View -->
    <mobile-data-cards
      v-else
      :headers="mobileHeaders"
      :items="users"
      :loading="loading"
      item-key="user_id"
      :fields-per-row="2"
      :bold-fields="['user_name']"
      :show-row-number="false"
      class="px-3"
      @click:item="openUserDialog"
    >
      <template v-slot:item.user_name="{ item }">
        {{ toLatin(item.user_name) }}
      </template>
      <template v-slot:item.orders_total="{ item }">
        {{ formatMoneyField(item, "orders_total") }}
      </template>
      <template v-slot:item.spent_on_credits="{ item }">
        {{ formatMoneyField(item, "spent_on_credits") }}
      </template>
      <template v-slot:item.current_credit_balance="{ item }">
        {{ formatMoneyField(item, "current_credit_balance") }}
      </template>
    </mobile-data-cards>

    <!-- User Order Details Dialog -->
    <credit-user-details-dialog-limit-transactions
      v-model="showUserDialog"
      :user="selectedUser"
    />

    <!-- Credit History Dialog -->
    <credit-user-history-dialog
      v-model="showHistoryDialog"
      :user="selectedHistoryUser"
      @navigate-to-topup="handleNavigateToTopup"
    />
  </div>
</template>

<script>
import limitService from "@/services/limit";
import CreditUserDetailsDialogLimitTransactions from "./CreditUserDetailsDialogLimitTransactions.vue";
import CreditUserHistoryDialog from "./CreditUserHistoryDialog.vue";
import hasSnackbar from "@/mixins/hasSnackbar";
import MobileDataCards from "@/components/global/MobileDataCards.vue";
import hasPrinting from "@/mixins/hasPrinting";
import hasReportPrinting from "@/mixins/report/hasReportPrinting";
import hasExcel from "@/mixins/hasExcel";

export default {
  name: "CompanyMultiRestaurantTabCreditUsers",
  components: {
    CreditUserDetailsDialogLimitTransactions,
    CreditUserHistoryDialog,
    MobileDataCards,
  },
  mixins: [hasSnackbar, hasPrinting, hasReportPrinting, hasExcel],
  data: () => ({
    report: null,
    loading: false,
    selectedUser: null,
    showUserDialog: false,
    showHistoryDialog: false,
    selectedHistoryUser: null,
  }),
  computed: {
    filters() {
      return this.$store.state.report.filters;
    },
    dates() {
      const { from, to } = this.$store.state.report.filters.dates;
      return { from, to };
    },
    users() {
      if (!this.report) return [];
      const userGroups = Array.isArray(this.report) ? this.report : [];
      return userGroups.map((group, index) => {
        const topUser = group.user || {};
        const hasRestaurantFilter = this.selectedRestaurants.length > 0;
        const allTxs = (group.limitTransactions || []).filter((t) => {
          if (t.company_id !== this.companyId) return false;
          if (hasRestaurantFilter && t.restaurant_id !== null) {
            return this.selectedRestaurants.includes(t.restaurant_id);
          }
          return true;
        });
        const txs = allTxs.filter((t) => {
          const txDate = (t.date || "").substring(0, 10);
          return txDate >= this.dates.from && txDate <= this.dates.to;
        });
        const userFromTx = allTxs.length > 0 ? allTxs[0].user : null;
        const userName = userFromTx
          ? [userFromTx.name, userFromTx.lastname].filter(Boolean).join(" ")
          : topUser.name || "";
        const creditTxs = txs.filter((t) => t.orderable_id === null);
        const orderTxs = txs.filter((t) => t.orderable_id !== null);
        const uniqueOrderIds = new Set(orderTxs.map((t) => t.order_id).filter(Boolean));

        const current_credit_balance = allTxs.reduce(
          (s, t) => s + this.parseAmount(t.total_amount ?? t.price),
          0
        );
        const spent_on_credits = orderTxs.reduce(
          (s, t) => s + Math.abs(this.parseAmount(t.total_amount ?? t.price)),
          0
        );
        const orders_total = spent_on_credits;
        return {
          num: index + 1,
          user_id: topUser.id,
          user_name: userName,
          orders_count: uniqueOrderIds.size,
          orders_valid: [],
          creditTransactions: creditTxs,
          orderTransactions: orderTxs,
          totals: {
            orders_total,
            orders_total_cents: null,
            spent_on_credits,
            spent_on_credits_cents: null,
            current_credit_balance,
            current_credit_balance_cents: null,
          },
        };
      });
    },
    companyId() {
      return this.$store.state.company?.id || null;
    },
    restaurants() {
      return this.$store.state.company?.restaurants || [];
    },
    selectedRestaurants() {
      return this.$store.state.report.selectedRestaurants || [];
    },
    headers() {
      return [
        { text: this.$t("r_b"), value: "num", sortable: false, print: false },
        { text: this.$t("user_id"), value: "user_id", sortable: false },
        { text: this.$t("fullname"), value: "user_name", sortable: false },
        { text: this.$t("orders"), value: "orders_count", align: "center" },
        { text: this.$t("orders_total") + ` (${this.majorityRestaurantCurrency})`, value: "orders_total", align: "right" },
        { text: this.$t("spent_on_credits") + ` (${this.majorityRestaurantCurrency})`, value: "spent_on_credits", align: "right" },
        {
          text: this.$t("credit_balance") + ` (${this.majorityRestaurantCurrency})`,
          value: "current_credit_balance",
          align: "right",
        },
        {
          text: this.$t("report"),
          value: "action",
          sortable: false,
          print: false,
          align: "center",
          width: "150px",
        },
      ];
    },
    mobileHeaders() {
      return [
        { text: this.$t("user_id"), value: "user_id" },
        { text: this.$t("fullname"), value: "user_name" },
        { text: this.$t("orders"), value: "orders_count" },
        { text: this.$t("orders_total") + ` (${this.majorityRestaurantCurrency})`, value: "orders_total" },
        { text: this.$t("spent_on_credits") + ` (${this.majorityRestaurantCurrency})`, value: "spent_on_credits" },
        { text: this.$t("credit_balance") + ` (${this.majorityRestaurantCurrency})`, value: "current_credit_balance" },
      ];
    },
  },
  methods: {
    async getUsers() {
      if (!this.companyId) {
        this.report = null;
        return;
      }
      this.loading = true;
      this.report = null;
      try {
        const { data } = await limitService.getCompanyTransactions(this.companyId, {
          date: this.dates.from,
          date2: this.dates.to,
        });
        this.report = data;
      } catch (error) {
        this.report = null;
        this.showErrorSnackbar(error, this.$t("error_loading_report"));
      } finally {
        this.loading = false;
      }
    },
    openUserDialog(item) {
      const orders_valid = Array.isArray(item.orders)
        ? item.orders.filter((o) => o && o.id)
        : item.orders_valid || [];
      this.selectedUser = { ...item, orders_valid };
      this.showUserDialog = true;
    },
    openHistoryDialog(item) {
      const rawGroup = (Array.isArray(this.report) ? this.report : []).find(
        (g) => (g.user?.id ?? null) === item.user_id
      );
      const allTxs = rawGroup?.limitTransactions || [];
      this.selectedHistoryUser = {
        ...item,
        creditTransactions: allTxs,
      };
      this.showHistoryDialog = true;
    },
    navigateToTopup(item) {
      // Emit event to parent component to switch tab and preselect user
      this.$emit("navigate-to-topup", item.user_id);
    },
    handleNavigateToTopup(userId) {
      // Handle event from history dialog
      this.$emit("navigate-to-topup", userId);
    },
    parseAmount(value) {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : 0;
    },
    formatMoneyCents(cents) {
      if (cents === null || cents === undefined || cents === "") return "";
      const n = Number(cents);
      return Number.isNaN(n) ? "" : (n / 100).toFixed(2);
    },
    formatMoneyFromTotals(centsVal, fallback) {
      if (centsVal !== null && centsVal !== undefined && centsVal !== "")
        return this.formatMoneyCents(centsVal);
      if (fallback !== null && fallback !== undefined && fallback !== "") {
        const n = Number(fallback);
        return Number.isNaN(n) ? "" : n.toFixed(2);
      }
      return "";
    },
    formatMoneyWithCurrency(item, field) {
      const val = this.formatMoneyField(item, field);
      return val ? val + " " + this.majorityRestaurantCurrency : "";
    },
    formatMoneyField(item, field) {
      const totals = item.totals || {};
      return this.formatMoneyFromTotals(totals[`${field}_cents`], totals[field]);
    },
    getExportHeaders() {
      return this.headers.filter((h) => h.print !== false);
    },
    formatUsersForExport() {
      const moneyFields = ["orders_total", "spent_on_credits", "current_credit_balance"];
      return this.users.map((item) => {
        const exportItem = {};
        this.getExportHeaders().forEach((header) => {
          if (moneyFields.includes(header.value)) {
            exportItem[header.value] = this.formatMoneyField(item, header.value);
          } else {
            exportItem[header.value] = item[header.value] || "";
          }
        });
        return exportItem;
      });
    },
  },
  watch: {
    companyId() {
      this.getUsers();
    },
    dates: {
      handler() {
        this.getUsers();
      },
      deep: true,
    },
    isExcelDownloading() {
      if (!this.isExcelDownloading) return;
      this.downloadExcel({
        fileName: [this.$t("report"), this.$t("credit_users")].join(" ") + ".xlsx",
        sheets: [
          {
            name: this.$t("credit_users"),
            headers: this.getExportHeaders(),
            items: this.formatUsersForExport(),
            title: [this.$t("report"), this.$t("credit_users")].join(" "),
            subtitle: this.subtitle,
          },
        ],
      });
    },
    isPrinting(isPrinting) {
      if (!isPrinting) return;
      const restaurantNames = this.selectedRestaurants
        .map((id) => this.restaurants.find((r) => r.id === id)?.name)
        .filter(Boolean)
        .map(this.toLatin)
        .join(", ");

      this.printDocument({
        tables: [
          {
            headers: this.getExportHeaders(),
            items: this.formatUsersForExport(),
            showCaption: true,
          },
        ],
        title: [this.$t("report"), this.$t("credit_users")].join(": "),
        subtitle: this.subtitle,
        afterTableHtml: `<div style="margin-top: 20px; padding-top: 10px; border-top: 1px solid #ddd;">
          <strong>${this.$t("restaurants")}:</strong> ${restaurantNames}
        </div>`,
        hideDatetime: true,
      });
    },
  },
  mounted() {
    this.getUsers();
  },
};
</script>
